// Boot: pick the sign-in path (Lagos Life frame, staging dev login, or "open it in Lagos Life"),
// then route between the menu and a park. Identity comes from the platform session, never cookies.
import { isFramed, VatarClientError, vatar } from '@wahala/vatar-client';
import { lazy, Suspense, useEffect } from 'react';
import { ParkSession } from './game/session';
import { takeAdClick } from './lib/ad';
import { ApiError, api, type JoinTicket, loadSession, saveSession, setPlatformMode } from './lib/api';
import { takeCampaign } from './lib/campaign';
import { restoreCheckouts, restorePurchases } from './lib/coins';
import { startErrors } from './lib/errors';
import { loadGuestKey, saveGuestKey } from './lib/guest';
import { claimInstallLink, reportInstalls, takeInstallLink } from './lib/install';
import { claimInvite, takeInvite } from './lib/invite';
import { parkNaira } from './lib/money';
import { setPaused } from './lib/pause';
import { heldOpening, refreshPush, takeOpening } from './lib/push';
import { startTelemetry } from './lib/telemetry';
import { PracticeCards } from './practice/Coach';
import { Login } from './screens/Login';
import { Menu } from './screens/Menu';
import { Outside } from './screens/Outside';
import { Pass } from './screens/Pass';
import { Shop } from './screens/Shop';
import { Today } from './screens/Today';
import { useUi } from './store/ui';
import { S } from './strings';
import { InstallBar } from './ui/InstallBar';
import { InstallSheet } from './ui/InstallSheet';
import { ParkLoading } from './ui/ParkLoading';
import { PushAsk } from './ui/PushAsk';
import type { ParkLoadInfo } from './ui/parkLoad';

// The round's screens (the HUD, the meeting, Owa, results) are their own chunk, so the menu opens
// without them: fetched once the menu is up and again on the way into a park, so a round never waits.
const loadPark = () => import('./screens/Park');
const Park = lazy(() => loadPark().then((m) => ({ default: m.Park })));
// Rankings and your record are their own chunk too: fetched when the board opens
// screens few players open on a visit load when opened, which keeps the first screen inside its budget
const Profile = lazy(() => import('./screens/Profile').then((m) => ({ default: m.Profile })));
const History = lazy(() => import('./screens/History').then((m) => ({ default: m.History })));
const People = lazy(() => import('./screens/People').then((m) => ({ default: m.People })));
const Board = lazy(() => import('./screens/Board').then((m) => ({ default: m.Board })));
const Compete = lazy(() => import('./screens/Compete').then((m) => ({ default: m.Compete })));
export const preloadPark = (): void => {
  loadPark().catch(() => {});
};
// the leave rules' boards load only when one is needed (never in the shell)
const CooldownBoard = lazy(() => import('./ui/LeaveRound').then((m) => ({ default: m.CooldownBoard })));

/** Into a park with a seat. `known` is what the way in already knew (its game type, this keke's look). */
export function enterPark(t: JoinTicket, known: ParkLoadInfo = {}): void {
  preloadPark();
  const ui = useUi.getState();
  ui.park?.leave();
  const session = new ParkSession(t, (reason) => {
    const u = useUi.getState();
    if (u.park !== session) return;
    // a cash run leaves for the cash hub, where its runs and the board are
    const screen = session.world.lobby?.cash ? 'compete' : 'menu';
    u.set({ park: null, screen, notice: reason === 'left' ? null : (S.errors[reason] ?? null) });
  });
  // the office's first-play park money, paid with this ticket: said once as the park opens
  if (t.welcome)
    session.world.toast(
      [
        t.welcome.coins > 0 ? S.promo.welcome(parkNaira(t.welcome.coins)) : '',
        t.welcome.passPremium ? S.promo.welcomePass : '',
      ]
        .filter(Boolean)
        .join(' '),
      'done',
      9000,
    );
  ui.set({
    park: session,
    screen: 'park',
    notice: null,
    joining: false,
    entering: { ...(ui.me?.name ? { driver: ui.me.name } : {}), ...known, code: t.code },
  });
  // test mode only: let the browser tests look inside (never in a live build's mode)
  if (ui.config?.mode === 'test') (window as unknown as { __wahala?: ParkSession }).__wahala = session;
}

/** Signed in with an invite link's code waiting: go straight to that park (a full or gone park says so on the menu). */
export async function joinInvite(): Promise<void> {
  const code = claimInvite();
  if (!code) return;
  try {
    enterPark(await api.joinPark(code));
  } catch (e) {
    useUi.getState().set({ notice: errorText(e) });
  }
}

export function errorText(e: unknown): string {
  if (e instanceof VatarClientError) return S.vatarErrors[e.code] ?? S.errors[e.code] ?? S.errors.generic ?? '';
  // the leave rules' wait is its own board with the time left, never a line of notice
  if (e instanceof ApiError && e.code === 'leave_cooldown') {
    useUi.getState().set({ cooldownUntil: Date.now() + Math.max(0, Number(e.body.waitMs) || 0) });
    return '';
  }
  const code = e instanceof ApiError ? e.code : 'generic';
  return S.errors[code] ?? S.errors.generic ?? '';
}

async function boot(): Promise<void> {
  const ui = useUi.getState();
  const config = await api.config();
  ui.set({ config });
  // test mode only: the e2e specs start a Play alone round of any type the server takes (City Hustle's practice shift)
  if (config.mode === 'test')
    (window as unknown as { __startSolo?: (type: string) => void }).__startSolo = (type) =>
      void import('./practice/solo').then((m) => m.startSolo(type, 'surprise'));
  if (config.errors) void startErrors(config.errors).catch(() => {});
  if (config.telemetry) startTelemetry(config.telemetry.sampleRate);
  const framed = isFramed();
  // the vendor that frames us (Lagos Life), if any: its style layer is its own chunk, never the shell
  if ((framed && config.lagosLife) || config.mode === 'test')
    void import('./vendors')
      .then((v) =>
        v.applyVendor(
          v.pickVendor({ framed, lagosLife: config.lagosLife, test: config.mode === 'test', search: location.search }),
        ),
      )
      .catch(() => {});
  if (framed && config.lagosLife) {
    const init = await vatar.init();
    setPlatformMode(init.mode);
    const s = await api.exchange(init.session);
    saveSession(s);
    // the sandbox's test world (PF-03) has its own settings (fares, what it offers): read them there
    if (init.mode !== config.mode) ui.set({ config: await api.config() });
    // a pay or stake sheet, or the phone going to the background: stop rendering, sound and the
    // keke, keep the socket (lib/pause); on resume pick up where we were and catch up with the park
    void vatar.onPause(() => setPaused(true));
    void vatar.onResume(() => {
      setPaused(false);
      useUi.getState().park?.net.wake();
    });
    // a distributor's player: their profile is Lagos Life's, never offered a save of ours (PF-19)
    ui.set({ me: s.user, avatar: init.player.avatar ?? null, account: 'platform', screen: 'menu' });
    // coins paid for but not yet given (a reload, a trip to the payment page): claim them now
    restorePurchases().catch(() => {});
  } else {
    const standalone = config.platforms?.standalone === true;
    takeInvite();
    takeInstallLink();
    if (standalone) await takeAdClick();
    // the wahala.io link's campaign tags, kept for the player this phone makes (lib/campaign)
    if (standalone) takeCampaign();
    // a tapped notification (FE-51) or a wahala.io/play/{screen} link: what to show once signed in
    takeOpening();
    // a session from this visit, else (standalone) the key this phone kept from the last one
    const key = standalone ? loadGuestKey() : null;
    if (!loadSession() && key) {
      try {
        saveSession(await api.guestResume(key));
      } catch (e) {
        // a key the server no longer knows (deleted, or another server's): ask for a name again
        if (e instanceof ApiError && e.status === 401) saveGuestKey(null);
      }
    }
    if (loadSession()) {
      try {
        const me = await api.me();
        const opening = heldOpening();
        ui.set({ me, account: me.account ?? null, screen: opening && !me.park ? opening.screen : 'menu' });
        // this browser's notifications, checked in with the server (FE-51)
        void refreshPush().catch(() => undefined);
        // back from the payment page, or a website coin pack this phone paid for and never heard of
        if (standalone) void restoreCheckouts().catch(() => undefined);
        // a park this driver is already seated in comes first; else the invite they followed
        if (me.park) enterPark(await api.joinPark(me.park));
        else await joinInvite();
        return;
      } catch {
        saveSession(null);
      }
    }
    ui.set({ screen: standalone || config.devAuth ? 'login' : 'outside' });
  }
}

export function App() {
  const screen = useUi((s) => s.screen);
  const notice = useUi((s) => s.notice);
  const noticeTone = useUi((s) => s.noticeTone);
  const joining = useUi((s) => s.joining);
  const entering = useUi((s) => s.entering);
  const cooldownUntil = useUi((s) => s.cooldownUntil);
  const installSheet = useUi((s) => s.installSheet);
  const meId = useUi((s) => s.me?.id);

  useEffect(() => {
    boot().catch((e) => useUi.getState().set({ screen: 'outside', notice: errorText(e) }));
    const wake = () => {
      if (!document.hidden) useUi.getState().park?.net.wake();
    };
    document.addEventListener('visibilitychange', wake);
    // the tutorial's last card: straight into a real park
    const quick = () => {
      api
        .quickPlay()
        .then(enterPark)
        .catch((e) => useUi.getState().set({ notice: errorText(e) }));
    };
    window.addEventListener('wahala:quickplay', quick);
    return () => {
      document.removeEventListener('visibilitychange', wake);
      window.removeEventListener('wahala:quickplay', quick);
    };
  }, []);

  // the menu is up: fetch the round's screens while the player picks a game
  useEffect(() => {
    if (screen !== 'menu') return;
    const t = setTimeout(preloadPark, 1_500);
    return () => clearTimeout(t);
  }, [screen]);

  // a player is signed in: the app's install signs waiting on this phone go to the api (lib/install)
  useEffect(() => {
    if (meId) void reportInstalls();
  }, [meId]);

  // the website's Get the app (?install=1): the install sheet, once the first screen is up
  useEffect(() => {
    if (screen !== 'boot' && claimInstallLink()) useUi.getState().set({ installSheet: true });
  }, [screen]);

  // a notice says its piece and goes: after a few seconds, on a tap, or once the player types again
  useEffect(() => {
    if (!notice) return;
    const clear = () => {
      if (useUi.getState().notice === notice) useUi.getState().set({ notice: null });
    };
    const t = setTimeout(clear, Math.min(8_000, 3_000 + notice.length * 40));
    // a tick later, not inside the keystroke: clearing it there re-renders the field before React
    // reads the key, and the first letter typed after a notice was lost
    const typed = () => setTimeout(clear, 0);
    document.addEventListener('input', typed, true);
    return () => {
      clearTimeout(t);
      document.removeEventListener('input', typed, true);
    };
  }, [notice]);

  return (
    // night is the game; day is the menus, the shop and the lobby (01-brand). The park screen picks
    // its own: day in the lobby, night on the road.
    <div className="app" data-theme={screen === 'park' ? 'night' : 'day'}>
      {screen === 'boot' && <p className="boot">{S.loading}</p>}
      {screen === 'outside' && <Outside />}
      {screen === 'login' && <Login />}
      {screen === 'menu' && <Menu />}
      {screen === 'park' && (
        // the round's screens on their way: the way-in screen, its seat in hand
        <Suspense fallback={<ParkLoading info={entering ?? {}} done={1} />}>
          <Park />
        </Suspense>
      )}
      {screen === 'park' && <PracticeCards />}
      {screen === 'shop' && <Shop />}
      {screen === 'pass' && <Pass />}
      {screen === 'today' && <Today />}
      {screen === 'board' && (
        <Suspense fallback={<p className="boot">{S.rankings.loading}</p>}>
          <Board />
        </Suspense>
      )}
      {screen === 'compete' && (
        <Suspense fallback={<p className="boot">{S.compete.loading}</p>}>
          <Compete />
        </Suspense>
      )}
      {screen === 'history' && (
        <Suspense fallback={null}>
          <History />
        </Suspense>
      )}
      {screen === 'profile' && (
        <Suspense fallback={null}>
          <Profile />
        </Suspense>
      )}
      {screen === 'people' && (
        <Suspense fallback={null}>
          <People />
        </Suspense>
      )}
      {/* asking for a seat: the way into the park starts here, over the menu */}
      {joining && screen !== 'park' && <ParkLoading info={entering ?? {}} done={0} />}
      <InstallBar hidden={screen === 'park' || screen === 'boot' || installSheet || Boolean(notice)} />
      {installSheet && screen !== 'boot' && <InstallSheet />}
      <PushAsk hidden={screen !== 'menu' || joining || Boolean(notice)} />
      {cooldownUntil !== null && screen !== 'park' && (
        <Suspense fallback={null}>
          <CooldownBoard until={cooldownUntil} />
        </Suspense>
      )}
      {notice && screen !== 'park' && (
        <div role="status">
          <button
            type="button"
            className={`notice notice--${noticeTone}`}
            aria-label={`${notice} ${S.dismiss}`}
            onClick={() => useUi.getState().set({ notice: null })}
          >
            {notice}
          </button>
        </div>
      )}
    </div>
  );
}
